/**
 * 版本与更新检查（OTA）
 * ============================================================
 *
 * 先想清楚"更新"到底更新什么，否则会做出一个每天骚扰用户的功能。
 *
 * WebView 壳下有两层，性质完全不同：
 *
 *   ┌─ 网页层（H5 页面 / 386 条片段）────────────┐
 *   │ 在 CloudBase 上，改了 index.html+assets 就 │
 *   │ 自动生效。用户下次加载就是新版，**不需要做 │
 *   │ 任何事**。给这层做"更新提示"纯属骚扰。     │
 *   └────────────────────────────────────────────┘
 *
 *   ┌─ 壳层（APK 里的 MainActivity）──────────────┐
 *   │ 只有改原生代码（权限、起始地址、媒体设置）才 │
 *   │ 需要重装。这才是"有更新"应该提示的东西。    │
 *   └────────────────────────────────────────────┘
 *
 * 所以：红点只由「壳版本落后」触发，网页层版本只用于展示，不参与判定。
 *
 * 当前壳版本怎么来：MainActivity 往 UA 里追加了 `DialectGoApp/<版本>`，
 * 这里从 navigator.userAgent 解析出来。浏览器直接访问（网页版）没有这个标记，
 * 视为"没有壳"，也就不提示重装。
 */

import api from './api';

/** 当前运行版本信息缓存（供 BottomNav 红点与 ProfilePage 卡片共用） */
let updateState = {
  /** 当前壳版本（从 UA 解析），浏览器访问时为 null */
  current: null,
  /** 最新壳版本（来自 /api/update） */
  latest: null,
  webVersion: null,
  notes: [],
  apkUrl: '',
  apkSize: '',
  releasedAt: '',
  loaded: false,
  /** 是否有新壳可装 */
  hasUpdate: false,
};

const listeners = new Set();

/** 订阅更新状态变化（红点、版本卡片都靠它刷新） */
export function subscribeUpdate(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn(updateState);
    } catch {
      /* 订阅者报错不影响其它 */
    }
  });
}

/** 从 UA 解析当前壳版本，如 `DialectGoApp/1.0.0` → '1.0.0'；没有则 null */
export function detectCurrentVersion() {
  try {
    const m = /DialectGoApp\/([\d.]+)/.exec(navigator.userAgent || '');
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/** 语义化版本比较：a > b 返回 1，a === b 返回 0，a < b 返回 -1 */
export function compareVersion(a, b) {
  const pa = String(a || '').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '').split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x > y) return 1;
    if (x < y) return -1;
  }
  return 0;
}

/** 读取当前快照（组件首屏渲染用，避免先闪"无更新"） */
export function getUpdateState() {
  return updateState;
}

/**
 * 拉取最新版本并更新状态。
 *
 * 幂等：多次调用只发一次请求（并发去重），拿到结果后刷新缓存并通知订阅者。
 */
let inflight = null;
export async function refreshUpdate() {
  if (inflight) return inflight;
  inflight = (async () => {
    const current = detectCurrentVersion();
    try {
      const resp = await api.get('/api/update');
      const d = resp.data?.data || null;
      updateState = {
        current,
        latest: d?.latest || null,
        webVersion: d?.webVersion || null,
        notes: d?.notes || [],
        apkUrl: d?.apkUrl || '',
        apkSize: d?.apkSize || '',
        releasedAt: d?.releasedAt || '',
        loaded: true,
        // 只有"壳"（有 UA 标记）才参与红点判定；网页版永远不提示重装
        hasUpdate: current != null && d?.latest != null && compareVersion(d.latest, current) > 0,
      };
    } catch {
      // 拿不到就保持现状：宁可没提示，也不要报错打断页面
      updateState = { ...updateState, current, loaded: true, hasUpdate: false };
    }
    emit();
    return updateState;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
